import { useCallback, useEffect } from "react";
import { filterSearch } from "@/lib/columnFilters";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { PartiesClient } from "@/features/parties/PartiesClient";
import { listParties, partiesKeys, type PartiesSearch } from "@/features/parties/parties.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { rememberListSearch } from "@/lib/listState";

export const Route = createFileRoute("/_shell/parties/")({
  // Reading is a module of its own (Relaties → Partijen); changing is parties_write,
  // on the party card. Nobody creates a party here (S23).
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "parties")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): PartiesSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      ...filterSearch(search),
      q: str(search.q),
      role: str(search.role),
      isActive: str(search.isActive),
      cursor: str(search.cursor),
      limit: str(search.limit),
      sort: str(search.sort),
      order: str(search.order),
    };
  },
  component: PartiesScreen,
});

function PartiesScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();

  // The party card's back link returns to the list as it was left.
  useEffect(() => {
    rememberListSearch("/parties", search);
  }, [search]);

  const query = useQuery({
    queryKey: partiesKeys.list(search),
    queryFn: () => listParties(search),
    placeholderData: keepPreviousData,
  });

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({
        search: (prev) => applyParamUpdates(prev, updates),
        replace: true,
      });
    },
    [navigate],
  );

  const openParty = useCallback(
    (id: string) => {
      navigate({ to: "/parties/$id", params: { id } });
    },
    [navigate],
  );

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Parties">
      <PartiesClient
        initialItems={query.data?.data ?? []}
        pagination={query.data?.pagination ?? { limit: 50, hasNextPage: false, nextCursor: null }}
        search={search}
        updateParams={updateParams}
        onOpenParty={openParty}
        userId={session.userId}
        isFetching={query.isFetching}
        isLoading={query.isLoading}
      />
    </div>
  );
}
